import { authSelectors } from 'ducks/auth'
import { getCustomizedStyles } from 'myenergy/customizedStyles'
import parseDataToState from 'myenergy/parseDataToState'
import prepareProposalData from 'myenergy/prepareProposalData'
import { decompressProposalData } from 'projectSections/sections/proposal/utils'
import { useCallback, useMemo, useState } from 'react'
import { useErrorHandling, useNotify } from 'react-admin'
import { useSelector } from 'react-redux'
import restClient from 'restClient'

const EXPECTED_ERRORS = {
  OUTDATED_REQUEST: 'Outdated ajax request',
  TIME_OUT: 'Time out',
}

const restClientInstance = restClient(window.API_ROOT + '/api')
let latestAjaxSession = 0

export type FetchProposalDataArgs = { id: number; unsavedData?: Record<string, unknown> }
const fetchProposalData = async ({ id, unsavedData }: FetchProposalDataArgs) => {
  const method = unsavedData ? 'CUSTOM_POST' : 'CUSTOM_GET'
  const response = await restClientInstance(method, 'custom', {
    url: `user_logins/?project_ids=${id}&expo_enabled=1&compress_data=true`,
    data: unsavedData,
  })
  if (response.data?.length === 0) {
    throw new Error(`No project found (ID: ${id})`)
  }
  return response.data
}

const checkFullCalcsReady = async ({
  requestAjaxSession,
  fullResultsIdentifier,
  projectId,
  orgId,
  count = 0,
}: {
  requestAjaxSession: number
  fullResultsIdentifier: string
  projectId: number
  orgId: number
  count?: number
}) => {
  if (count > 10) {
    return Promise.reject(EXPECTED_ERRORS.TIME_OUT)
  }
  if (requestAjaxSession !== latestAjaxSession) {
    return Promise.reject(EXPECTED_ERRORS.OUTDATED_REQUEST)
  }
  return new Promise(async (res, rej) => {
    try {
      const response = await restClientInstance('CUSTOM_GET', 'custom', {
        url:
          `orgs/${orgId}/projects/${projectId}/full_calcs_for_unsaved/` +
          (fullResultsIdentifier ? `?full_results_identifier=${fullResultsIdentifier}` : ''),
      })
      const status = response.data.status
      if (status === 'in_progress' && response?.data?.progress) {
        setTimeout(() => {
          res(checkFullCalcsReady({ requestAjaxSession, fullResultsIdentifier, projectId, orgId, count: count + 1 }))
        }, 3000)
      } else {
        res(response.data)
      }
    } catch (error: any) {
      if (error.status === 404) {
        setTimeout(() => {
          res(checkFullCalcsReady({ requestAjaxSession, fullResultsIdentifier, projectId, orgId, count: count + 1 }))
        }, 3000)
      } else {
        rej('Unknown Error')
      }
    }
  })
}

const mapRawProposalDataToState = (data) => {
  const state = parseDataToState(decompressProposalData(data))
  state.myeStyles = getCustomizedStyles(state.selectedProject.proposal_data.colors)
  return state
}

const useProposalData = () => {
  const [state, setState] = useState<any>(undefined)
  const [error, setError] = useState<string | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const notify = useNotify()
  const orgId = useSelector(authSelectors.getOrgId)
  const errorHandling = useErrorHandling()

  const updateState = useCallback((newState) => {
    if (!newState.selectedSystem) {
      setError('No available systems')
    } else {
      setError(undefined)
    }
    setState(newState)
  }, [])

  const maybePollingFullCalcsData = useCallback(
    async ({ data, requestAjaxSession, projectId }) => {
      const project = data[0].projects[0]
      const fullResultsIdentifier = data[0].full_results_identifier
      const simulateFirstYearOnly = project.simulate_first_year_only
      if (simulateFirstYearOnly === true && fullResultsIdentifier && orgId) {
        try {
          const data = await checkFullCalcsReady({ fullResultsIdentifier, requestAjaxSession, projectId, orgId })
          const state = mapRawProposalDataToState(data)
          updateState(state)
        } catch (e) {
          // Handle outdated ajax request gracefully - this is expected when a new request is made
          if (Object.values(EXPECTED_ERRORS).includes(String(e))) {
            console.warn('Ignoring polling full calc error. Error:', e)
            return
          }
          // Log and handle unexpected errors
          console.error(e)
          errorHandling.unexpected(new Error('Failed on polling full calc data'))
        }
      }
    },
    [orgId]
  )

  const loadProposalData = useCallback(async (args: FetchProposalDataArgs) => {
    try {
      setIsLoading(true)
      const requestAjaxSession = ++latestAjaxSession
      const data = await fetchProposalData({ id: args.id, unsavedData: args.unsavedData })
      const state = mapRawProposalDataToState(data)
      updateState(state)
      setIsLoading(false)
      maybePollingFullCalcsData({ data, requestAjaxSession, projectId: args.id })
    } catch (e: any) {
      setIsLoading(false)
      notify(e.message, 'warning')
    }
  }, [])

  const handleSelectSystem = useCallback(
    (uuid: string) => {
      if (!state) return
      const system = state.systems.filter((system) => system.uuid === uuid)[0]
      const paymentOptionId = system.payment_options.find((po) => po.id === state.selectedPaymentOptionId)
        ? state.selectedPaymentOptionId
        : system.payment_options[0].id

      const newState = Object.assign({}, state, {
        selectedSystem: system,
        selectedPaymentOptionId: paymentOptionId,
      })
      updateState(newState)
    },
    [state]
  )

  const handleSelectPayment = useCallback(
    (id: number) => {
      if (!state) return
      const newState = Object.assign({}, state, {
        selectedPaymentOptionId: id,
      })
      updateState(newState)
    },
    [state]
  )

  const proposalData = useMemo(() => {
    if (!state) return undefined
    return prepareProposalData(state)
  }, [state])

  return useMemo(() => {
    return {
      error,
      data: proposalData,
      isLoading,
      isNotValidProject: proposalData === null,
      loadProposalData,
      handleSelectPayment,
      handleSelectSystem,
    }
  }, [proposalData, error, isLoading, loadProposalData, handleSelectSystem, handleSelectPayment])
}

export default useProposalData
